import { useMemo } from 'react';
import { Stack, Typography } from '@mui/material';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { QuotaBalanceDto } from '@/types/dtos/entitlements/quota-balance.dto';
import { useQuotaBalances } from '@/state/queries/entitlements/use-quota-balances';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import type { EventPlanCapacityUsageMode } from '@/constants/event-plan-widget-contexts';
import { resolveFlowDurationMinutes } from '@/helpers/flow-playout-hours.helper';

const QUOTA_CONTRIBUTORS = 'contributors';
const QUOTA_DATA_TRANSFER_GB = 'data_transfer_gb';
const QUOTA_PLAYOUT_HOURS = 'playout_hours';

/** Warn when remaining capacity is under 10% of granted. */
const WARN_AVAILABLE_RATIO = 0.1;

type Tone = 'ok' | 'warn' | 'error';

type CapacityLine = {
  key: string;
  text: string;
  tone: Tone;
};

function findBalance(balances: QuotaBalanceDto[] | undefined, quotaKey: string): QuotaBalanceDto | undefined {
  return balances?.find((b) => b.quotaKey === quotaKey);
}

function formatGb(n: number): string {
  if (!Number.isFinite(n)) return '0';
  const rounded = Math.round(n * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function formatHoursLabel(hours: number): string {
  if (!Number.isFinite(hours)) return '0';
  const rounded = Math.round(hours * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function toneForRemaining(totalGranted: number, available: number): Tone {
  if (available <= 0) return 'error';
  if (totalGranted > 0 && available / totalGranted < WARN_AVAILABLE_RATIO) return 'warn';
  return 'ok';
}

function CapacityRow({ text, tone }: { text: string; tone: Tone }) {
  const iconSx = { fontSize: 18, flexShrink: 0, mt: '1px' } as const;
  const iconColor = tone === 'error' ? 'error.main' : tone === 'warn' ? 'warning.main' : 'text.secondary';

  return (
    <Stack direction="row" spacing={0.75} alignItems="flex-start">
      {tone === 'error' ? (
        <ErrorOutlineOutlined sx={{ ...iconSx, color: iconColor }} />
      ) : tone === 'warn' ? (
        <WarningAmberOutlined sx={{ ...iconSx, color: iconColor }} />
      ) : (
        <CheckCircleOutlined sx={{ ...iconSx, color: iconColor }} />
      )}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ lineHeight: 1.4, fontWeight: tone === 'error' ? 600 : 400 }}
      >
        {text}
      </Typography>
    </Stack>
  );
}

/**
 * Quota usage under a plan line.
 * `usageMode` selects which rows to show for the product line in this widget context.
 */
export default function BasePlanCapacityUsage({
  flow,
  capacityScopeType,
  capacityScopeId,
  flowScopeId,
  usageMode = 'full',
}: {
  flow?: FlowDto;
  capacityScopeType: string;
  capacityScopeId: string;
  /** Origin flow id for playout when capacity scope is Event (legacy) or when comparing flow length. */
  flowScopeId?: string;
  usageMode?: Exclude<EventPlanCapacityUsageMode, 'none'>;
}) {
  const t = useTranslations();
  const showCrewUpload = usageMode === 'full' || usageMode === 'crew_upload';
  const showPlayout = usageMode === 'full' || usageMode === 'playout';

  const { data: capacityBalances } = useQuotaBalances(capacityScopeType, capacityScopeId);
  const playoutScopeId = capacityScopeType === 'Flow' ? capacityScopeId : flowScopeId?.trim() ? flowScopeId : undefined;
  const needsSeparateFlowBalances = showPlayout && capacityScopeType !== 'Flow' && Boolean(playoutScopeId);
  const { data: flowBalances } = useQuotaBalances(
    needsSeparateFlowBalances ? 'Flow' : undefined,
    needsSeparateFlowBalances ? playoutScopeId : undefined,
  );

  const lines = useMemo((): CapacityLine[] => {
    const out: CapacityLine[] = [];
    const translate = t as (key: TranslationKey, params?: Record<string, string | number>) => string;
    const balances = capacityBalances;
    const playoutBalances = capacityScopeType === 'Flow' ? capacityBalances : flowBalances;

    if (showCrewUpload) {
      const contributors = findBalance(balances, QUOTA_CONTRIBUTORS);
      if (contributors && contributors.totalGranted > 0) {
        const used = contributors.totalConsumed;
        const total = contributors.totalGranted;
        const available = Math.max(0, contributors.available);
        out.push({
          key: QUOTA_CONTRIBUTORS,
          text: translate('event.plan.capacityContributorsUsed', {
            used,
            total,
            available,
          }),
          tone: toneForRemaining(total, available),
        });
      }

      const upload = findBalance(balances, QUOTA_DATA_TRANSFER_GB);
      if (upload && upload.totalGranted > 0) {
        const used = upload.totalConsumed;
        const total = upload.totalGranted;
        const available = typeof upload.effectiveAvailable === 'number' ? upload.effectiveAvailable : upload.available;
        const availableClamped = Math.max(0, available);
        out.push({
          key: QUOTA_DATA_TRANSFER_GB,
          text: translate('event.plan.capacityMediaUploadUsed', {
            used: formatGb(used),
            total: formatGb(total),
            available: formatGb(availableClamped),
          }),
          tone: toneForRemaining(total, availableClamped),
        });
      }
    }

    if (showPlayout && playoutScopeId && flow) {
      const playout = findBalance(playoutBalances, QUOTA_PLAYOUT_HOURS);
      const playoutHours = playout?.totalGranted ?? 0;
      const flowMinutes = Math.max(0, Math.round(resolveFlowDurationMinutes(flow)));
      const flowHours = flowMinutes / 60;
      const hours = Math.floor(flowMinutes / 60);
      const minutes = flowMinutes % 60;
      const hoursLabel = formatHoursLabel(playoutHours);

      if (playout || flowMinutes > 0) {
        const exceeds = flowHours > playoutHours + 1e-9;
        const lengthParams = { hours, minutes, playoutHours: hoursLabel };
        out.push({
          key: QUOTA_PLAYOUT_HOURS,
          text: exceeds
            ? translate('event.plan.capacityPlayoutExceeds', lengthParams)
            : translate('event.plan.capacityPlayoutFits', lengthParams),
          tone: exceeds ? 'error' : 'ok',
        });
      }
    }

    return out;
  }, [capacityBalances, capacityScopeType, flow, flowBalances, playoutScopeId, showCrewUpload, showPlayout, t]);

  if (!lines.length) return null;

  return (
    <Stack spacing={0.5} sx={{ mt: 0.25 }}>
      {lines.map((line) => (
        <CapacityRow key={line.key} text={line.text} tone={line.tone} />
      ))}
    </Stack>
  );
}
